import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { ArrowLeft, Loader2, Mail, MessageSquare, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem } from "@/components/ui/form";
import { PinInput } from "@/components/ui/pin-input";
import { apiRequest } from "@/lib/queryClient";

/**
 * DS-1 — the step between "we found your account" and "choose a PIN".
 *
 * A first-time PIN (and the one after an admin "Reset PIN") is only accepted
 * with a one-time code: texted to the phone on file, or issued by the supplier
 * from the account page when that phone cannot receive texts. This step asks
 * for the text, takes the code, and checks it with the server before the buyer
 * is asked to think of a PIN.
 *
 * The server never says whether a text actually went out (it would turn the
 * endpoint into a phone-number oracle), so the ways forward for someone whose
 * phone cannot receive one — an emailed link, a code from the supplier — are
 * always on screen rather than shown in response to a failure.
 */

export const PIN_CODE_ERROR_CODES = [
  "PIN_CODE_REQUIRED",
  "PIN_CODE_INVALID",
  "PIN_CODE_EXPIRED",
  "PIN_CODE_EXHAUSTED",
  "PIN_CODE_LOCKED",
] as const;

export interface ApiError {
  status: number | null;
  message?: string;
  code?: string;
  attemptsRemaining?: number;
}

/** apiRequest throws `"<status>: <body>"` on any non-2xx; dig the server's own words out. */
export function parseApiError(error: unknown): ApiError {
  const raw = String((error as Error)?.message ?? "");
  const status = /^(\d{3}):/.exec(raw)?.[1];
  try {
    const body = JSON.parse(raw.replace(/^\d+:\s*/, ""));
    return {
      status: status ? Number(status) : null,
      message: typeof body?.message === "string" ? body.message : undefined,
      code: typeof body?.code === "string" ? body.code : undefined,
      attemptsRemaining: typeof body?.attemptsRemaining === "number" ? body.attemptsRemaining : undefined,
    };
  } catch {
    return { status: status ? Number(status) : null };
  }
}

const RESEND_COOLDOWN_SECONDS = 30;

const codeSchema = z.object({
  code: z.string().regex(/^\d{6}$/, "Enter all 6 digits of the code."),
});
type CodeForm = z.infer<typeof codeSchema>;

interface PinSetupCodeStepProps {
  accountId: number;
  contactId: number | null;
  /** The number the buyer signed in with, as typed. */
  phone: string;
  vendorName: string;
  /** The account has an email on file, so the emailed set-up link is an option. */
  canEmailLink: boolean;
  onEmailLink: () => void;
  onVerified: (code: string) => void;
  onBack: () => void;
  backLabel: string;
  /** Set when set-pin bounced the buyer back here (the code expired while they chose a PIN). */
  initialError?: string | null;
  size?: "default" | "kiosk";
  disabled?: boolean;
}

export function PinSetupCodeStep({
  accountId,
  contactId,
  phone,
  vendorName,
  canEmailLink,
  onEmailLink,
  onVerified,
  onBack,
  backLabel,
  initialError = null,
  size = "default",
  disabled = false,
}: PinSetupCodeStepProps) {
  const [sending, setSending] = useState(true);
  const [checking, setChecking] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(initialError);
  const [cooldown, setCooldown] = useState(0);
  // Bumped whenever the field is cleared, so the input remounts with the
  // cursor back in the first box.
  const [inputKey, setInputKey] = useState(0);
  const requestedRef = useRef(false);

  const form = useForm<CodeForm>({
    resolver: zodResolver(codeSchema),
    defaultValues: { code: "" },
  });

  const phoneDigits = phone.replace(/\D/g, "");
  const subject = { accountId, ...(contactId !== null ? { contactId } : {}), phone: phoneDigits };

  const requestCode = async (isResend: boolean) => {
    setSending(true);
    if (isResend) setError(null);
    try {
      await apiRequest("POST", "/api/customer/request-pin-code", subject);
      setNotice(isResend ? "We sent a new code. The earlier one no longer works." : null);
      setCooldown(RESEND_COOLDOWN_SECONDS);
      form.reset({ code: "" });
      setInputKey((k) => k + 1);
    } catch (err) {
      const parsed = parseApiError(err);
      setNotice(null);
      setError(
        parsed.status === 429
          ? parsed.message || "Too many codes requested. Please wait a while before asking for another."
          : "We couldn't send a code just now. Check your connection and try again.",
      );
    } finally {
      setSending(false);
    }
  };

  // Ask for the text once, on arrival. The ref survives React's development
  // double-invoke of effects, so one arrival is one text.
  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;
    void requestCode(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onSubmit = async ({ code }: CodeForm) => {
    setChecking(true);
    setError(null);
    try {
      await apiRequest("POST", "/api/customer/verify-pin-code", { ...subject, code });
      onVerified(code);
    } catch (err) {
      const parsed = parseApiError(err);
      form.reset({ code: "" });
      setInputKey((k) => k + 1);
      setNotice(null);
      setError(parsed.message || "We couldn't check that code. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  const onInvalid = (errors: unknown) => {
    console.error("🚫 RHF validation failed:", errors);
    setError("Enter all 6 digits of the code.");
  };

  const submit = form.handleSubmit(onSubmit, onInvalid);
  const busy = sending || checking || disabled;
  const kiosk = size === "kiosk";
  const primaryButton = kiosk
    ? "w-full h-14 text-lg bg-cn-brand-blue hover:bg-cn-brand-blue/90"
    : "w-full h-12 text-lg bg-[#2B5397] hover:bg-blue-100 hover:text-[#2B5397] text-white border border-[#2B5397] hover:border-blue-100";
  const linkClass = kiosk ? "text-sm text-cn-brand-blue hover:underline" : "text-sm text-[#2B5397] hover:underline";

  return (
    <Form {...form}>
      <form onSubmit={submit} className="space-y-5" data-testid="form-pin-setup-code" noValidate>
        <p className="text-sm text-gray-600 text-center" data-testid="text-code-sent">
          {sending && !notice ? (
            <>Sending a code to <strong>{phone}</strong>…</>
          ) : (
            <>
              If <strong>{phone}</strong> can receive texts, a 6-digit code is on its way. It can take a minute.
            </>
          )}
        </p>

        <FormField
          control={form.control}
          name="code"
          render={({ field }) => (
            <FormItem>
              <FormControl>
                <PinInput
                  key={inputKey}
                  length={6}
                  value={field.value}
                  onChange={(value) => {
                    field.onChange(value);
                    if (error) setError(null);
                  }}
                  onComplete={() => void submit()}
                  size={kiosk ? "default" : "sm"}
                  mask={false}
                  autoFocus
                  disabled={checking || disabled}
                  data-testid="input-setup-code"
                />
              </FormControl>
            </FormItem>
          )}
        />

        {error && (
          <p role="alert" className="text-sm text-red-600 text-center" data-testid="text-code-error">
            {error}
          </p>
        )}
        {notice && !error && (
          <p role="status" className="text-sm text-green-700 text-center" data-testid="text-code-notice">
            {notice}
          </p>
        )}

        <Button type="submit" className={primaryButton} disabled={busy || form.watch("code").length < 6} data-testid="button-verify-code">
          {checking ? (
            <>
              <Loader2 className="h-5 w-5 mr-2 animate-spin" />
              Checking...
            </>
          ) : (
            <>
              <ShieldCheck className="h-5 w-5 mr-2" />
              Continue
            </>
          )}
        </Button>

        <div className="rounded-lg bg-gray-50 border border-gray-100 px-4 py-3 space-y-2 text-center">
          <p className="text-sm font-medium text-gray-700">Didn't get a text?</p>
          <div>
            <button
              type="button"
              onClick={() => void requestCode(true)}
              disabled={busy || cooldown > 0}
              className={`${linkClass} inline-flex items-center disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed`}
              data-testid="button-resend-code"
            >
              <MessageSquare className="h-3.5 w-3.5 mr-1.5" />
              {sending ? "Sending..." : cooldown > 0 ? `Send a new code in ${cooldown}s` : "Send a new code"}
            </button>
          </div>
          {canEmailLink && (
            <div>
              <button
                type="button"
                onClick={onEmailLink}
                disabled={busy}
                className={`${linkClass} inline-flex items-center disabled:text-gray-400`}
                data-testid="button-email-link-instead"
              >
                <Mail className="h-3.5 w-3.5 mr-1.5" />
                Email me a set-up link instead
              </button>
            </div>
          )}
          <p className="text-xs text-gray-500" data-testid="text-ask-supplier">
            Phone can't receive texts? Ask {vendorName} for a setup code and enter it above.
          </p>
        </div>

        <div className="text-center">
          <button
            type="button"
            onClick={onBack}
            className="text-sm text-gray-500 hover:underline inline-flex items-center"
            data-testid="link-code-back"
          >
            <ArrowLeft className="h-3 w-3 mr-1" />
            {backLabel}
          </button>
        </div>
      </form>
    </Form>
  );
}
